<template>
  <div
    ref="viewportRef"
    :class="[
      ns.e('viewport'),
      { [ns.is('has-footer', props.showFooter)]: props.showFooter },
      { [ns.is('with-scroll-x', showSeparateXScroll)]: showSeparateXScroll },
    ]"
    :style="viewportStyle"
  >
    <div ref="headerScrollRef" :class="ns.e('header-viewport')">
      <grid-header-row
        :fixed-type="''"
        :overflow-x="overflowX"
        :columns="layoutColumns"
        :content-width="effectiveLayoutContentWidth"
      />
    </div>

    <div
      :class="[
        ns.e('data-panel'),
        { [ns.is('with-scroll-x', showSeparateXScroll)]: showSeparateXScroll },
      ]"
    >
      <div
        ref="scrollRef"
        :class="[
          ns.e('body-wrapper'),
          { [ns.is('scroll-y', needsVerticalScroll)]: needsVerticalScroll },
          { [ns.is('fixed-x-scroll', showSeparateXScroll)]: showSeparateXScroll },
        ]"
        @scroll="onMainScroll"
      >
        <div :class="ns.e('body-content')" :style="bodyContentStyle">
          <div v-if="showEmptyState" :class="ns.e('empty')">
            {{ t('z.table.emptyText') }}
          </div>

          <grid-virtual-rows
            v-else
            fixed-type=""
            :overflow-x="overflowX"
            :columns="layoutColumns"
            :virtual-rows="virtualRows"
            :total-size="totalSize"
            :row-height="rowHeight"
            :table-width="effectiveLayoutContentWidth"
          />
        </div>
      </div>

      <div
        v-if="props.showFooter"
        ref="footerScrollRef"
        :class="ns.e('footer-viewport')"
      >
        <grid-footer
          :fixed-type="''"
          :overflow-x="overflowX"
          :columns="layoutColumns"
          :content-width="effectiveLayoutContentWidth"
        />
      </div>

      <div v-if="showSeparateXScroll" :class="ns.e('scroll-x-foot')">
        <transition name="z-fade-in-linear">
          <div v-if="showScrollHint" :class="ns.e('scroll-hint')">
            {{ t('z.table.horizontalScrollTip') }}
          </div>
        </transition>
        <div
          ref="scrollXRef"
          :class="ns.e('scroll-x')"
          @scroll="onScrollXBar"
        >
          <div
            :class="ns.e('scroll-x-inner')"
            :style="{ width: addUnit(scrollXContentWidth) }"
          />
        </div>
      </div>
    </div>

    <div
      v-if="showFixedOverlays"
      :class="[
        ns.e('fixed'),
        { [ns.is('with-scroll-x', showSeparateXScroll)]: showSeparateXScroll },
      ]"
    >
      <div
        v-if="leftFixedWidth > 0"
        :class="[
          ns.e('fixed-left'),
          ns.is('scrolling', leftScrolling),
        ]"
        :style="leftFixedPanelStyle"
      >
        <grid-header-row
          fixed-type=""
          :overflow-x="false"
          :columns="leftPanelLayoutColumns"
        />
        <div :class="ns.e('fixed-body')">
          <div :class="ns.e('fixed-body-inner')" :style="fixedBodyScrollTransform">
            <grid-virtual-rows
              v-if="tableData.length"
              fixed-type=""
              :overflow-x="false"
              :columns="leftPanelLayoutColumns"
              :virtual-rows="virtualRows"
              :total-size="totalSize"
              :row-height="rowHeight"
              :table-width="leftPanelTableWidth"
            />
            <grid-footer
              v-if="props.showFooter"
              fixed-type=""
              :overflow-x="false"
              :columns="leftPanelLayoutColumns"
            />
          </div>
        </div>
      </div>

      <div
        v-if="rightFixedWidth > 0"
        :class="[
          ns.e('fixed-right'),
          ns.is('scrolling', rightScrolling),
        ]"
        :style="{ width: addUnit(rightFixedWidth) }"
      >
        <div :class="ns.e('fixed-header-inner')">
          <grid-header-row
            fixed-type=""
            :overflow-x="false"
            :columns="rightPanelLayoutColumns"
          />
        </div>
        <div :class="ns.e('fixed-body')">
          <div :class="ns.e('fixed-body-inner')" :style="fixedBodyScrollTransform">
            <grid-virtual-rows
              v-if="tableData.length"
              fixed-type=""
              :overflow-x="false"
              :columns="rightPanelLayoutColumns"
              :virtual-rows="virtualRows"
              :total-size="totalSize"
              :row-height="rowHeight"
              :table-width="rightPanelTableWidth"
            />
            <grid-footer
              v-if="props.showFooter"
              fixed-type=""
              :overflow-x="false"
              :columns="rightPanelLayoutColumns"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import { getGridRowHeight } from '../row-height'
import { useHorizontalScroll } from '../hooks/use-horizontal-scroll'
import {
  computeFixedWidth,
  hasFixedColumns,
  isLeftFixedStuck,
  resolveLeftFixedLeadingOffset,
  resolveLeftFixedPanelOffset,
  resolveLeftPanelColumns,
  resolveRightPanelColumns,
  resolveTableWidth,
} from '../utils/fixed-column'
import { getRowId } from '../utils/row-id'
import {
  DEFAULT_EMPTY_BODY_HEIGHT,
  needsGridVerticalScroll,
  resolveBodyAreaHeight,
  resolveViewportHeight,
} from '../utils/resolve-viewport-height'
import {
  measureGridRenderedContentWidth,
  resolveEffectiveGridContentWidth,
  resolveGridBodyContentWidth,
  resolveGridLayoutWidth,
  resolveMaxGridScrollLeft,
  resolveScrollbarGutter,
} from '../utils/resolve-layout-width'
import { resolveInfiniteScroll } from '../utils/infinite-scroll'
import GridFooter from './grid-footer.vue'
import GridHeaderRow from './grid-header-row.vue'
import GridVirtualRows from './grid-virtual-rows.vue'
import type { DataGrid, GridColumnInfo } from '../types'
import type { CSSProperties } from 'vue'

const ns = useNamespace('grid')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const { reactData, internalData, props } = $grid

const viewportRef = ref<HTMLElement | null>(null)
const headerScrollRef = ref<HTMLElement | null>(null)
const footerScrollRef = ref<HTMLElement | null>(null)
const scrollRef = ref<HTMLElement | null>(null)
const scrollXRef = ref<HTMLElement | null>(null)
const overflowX = ref(false)
const renderedContentWidth = ref(0)
/** fill 模式下 body-wrapper 实测可视高度，用于判断纵向滚动 */
const measuredBodyHeight = ref(0)

const fillMode = computed(
  () =>
    props.autoHeight === false
    && (props.height == null || props.height === '')
)

const needsVerticalScroll = computed(() =>
  needsGridVerticalScroll({
    rowCount: reactData.tableData.length,
    rowHeight: rowHeight.value,
    height: props.height,
    showFooter: props.showFooter,
    useFixedXScroll: showSeparateXScroll.value,
    fill: fillMode.value,
    measuredBodyHeight: measuredBodyHeight.value,
  })
)

const { showScrollHint } = useHorizontalScroll({
  wheelRef: viewportRef,
  scrollRef,
  hintRef: scrollXRef,
  hintFallbackRef: scrollRef,
  canScroll: overflowX,
  canScrollVertically: needsVerticalScroll,
  resolveMaxScrollLeft: (el) => resolveMaxBodyScrollLeft(el),
})
const mainScrollTop = ref(0)
const mainScrollLeft = ref(0)
const leftScrolling = ref(false)
const rightScrolling = ref(false)
/** 垂直滚动条占位宽度，用于右固定列让出滚动条 */
const verticalScrollbarWidth = ref(0)

let resizeObserver: ResizeObserver | undefined
let programmaticScroll = false
let syncingScrollX = false

function syncHorizontalMirrors(left: number) {
  const headerEl = headerScrollRef.value
  if (headerEl && headerEl.scrollLeft !== left) {
    headerEl.scrollLeft = left
  }
  const footerEl = footerScrollRef.value
  if (footerEl && footerEl.scrollLeft !== left) {
    footerEl.scrollLeft = left
  }
}

function updateScrollbarPadding() {
  verticalScrollbarWidth.value = resolveScrollbarGutter(scrollRef.value)
}

function syncRenderedContentWidth() {
  renderedContentWidth.value = measureGridRenderedContentWidth({
    bodyScrollEl: scrollRef.value,
    headerScrollEl: headerScrollRef.value,
  })
}

async function refreshRenderedContentWidth() {
  await nextTick()
  syncRenderedContentWidth()
  const el = scrollRef.value
  if (!el) {
    return
  }
  const nextLeft = clampBodyScrollLeft(el, el.scrollLeft)
  if (nextLeft !== el.scrollLeft) {
    setBodyScrollLeft(nextLeft)
  }
}

function resolveMaxBodyScrollLeft(el: HTMLElement) {
  return resolveMaxGridScrollLeft(
    effectiveLayoutContentWidth.value,
    el.clientWidth
  )
}

function clampBodyScrollLeft(el: HTMLElement, left: number) {
  return Math.min(Math.max(0, left), resolveMaxBodyScrollLeft(el))
}

function setBodyScrollLeft(left: number) {
  const bodyEl = scrollRef.value
  if (!bodyEl) {
    return
  }
  const nextLeft = clampBodyScrollLeft(bodyEl, left)
  if (bodyEl.scrollLeft === nextLeft) {
    return
  }
  bodyEl.scrollLeft = nextLeft
  mainScrollLeft.value = nextLeft
  updateScrollShadows(bodyEl)
  syncScrollXBarLeft(nextLeft)
  syncHorizontalMirrors(nextLeft)
}

function syncScrollXBarLeft(left: number) {
  const barEl = scrollXRef.value
  if (!barEl || syncingScrollX) {
    return
  }
  const maxLeft = Math.max(0, barEl.scrollWidth - barEl.clientWidth)
  const nextLeft = Math.min(Math.max(0, left), maxLeft)
  if (barEl.scrollLeft === nextLeft) {
    return
  }
  syncingScrollX = true
  barEl.scrollLeft = nextLeft
  syncingScrollX = false
}

function syncBodyScrollLeftFromBar(left: number) {
  const bodyEl = scrollRef.value
  if (!bodyEl || syncingScrollX) {
    return
  }
  const nextLeft = clampBodyScrollLeft(bodyEl, left)
  if (bodyEl.scrollLeft === nextLeft) {
    return
  }
  syncingScrollX = true
  bodyEl.scrollLeft = nextLeft
  mainScrollLeft.value = nextLeft
  updateScrollShadows(bodyEl)
  syncHorizontalMirrors(nextLeft)
  syncingScrollX = false
}

function resolveRowIndex(row: Record<string, unknown>) {
  const rowId = getRowId(row)
  const byId = tableData.value.findIndex((item) => getRowId(item) === rowId)
  if (byId >= 0) {
    return byId
  }
  return tableData.value.findIndex((item) => item === row)
}

async function scrollToRow(row: Record<string, unknown>) {
  const el = scrollRef.value
  if (!el) {
    return
  }

  const rowIndex = resolveRowIndex(row)
  if (rowIndex < 0) {
    return
  }

  const align = resolveRowScrollAlign({
    rowIndex,
    rowHeight: rowHeight.value,
    scrollTop: el.scrollTop,
    clientHeight: el.clientHeight,
  })

  if (!align) {
    return
  }

  virtualizer.value.scrollToIndex(rowIndex, { align })
  await nextTick()
  virtualizer.value.measure()
}

async function scrollToColumn(
  column: GridColumnInfo,
  options?: { align?: 'start' | 'auto' }
) {
  const el = scrollRef.value
  if (!el || !overflowX.value) {
    return
  }

  const nextScrollLeft = resolveScrollLeftForColumn({
    columns: layoutColumns.value,
    column,
    scrollLeft: el.scrollLeft,
    clientWidth: el.clientWidth,
    leftFixedWidth: effectiveLeftFixedInset.value,
    rightFixedWidth: rightFixedWidth.value,
    align: options?.align ?? 'auto',
  })

  if (nextScrollLeft == null || nextScrollLeft === el.scrollLeft) {
    return
  }

  setBodyScrollLeft(nextScrollLeft)
  mainScrollTop.value = el.scrollTop
}

async function scrollToCell(row: Record<string, unknown>, column: GridColumnInfo) {
  programmaticScroll = true
  try {
    await scrollToRow(row)
    await scrollToColumn(column, { align: 'start' })
    await nextTick()
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve())
    })
    virtualizer.value.measure()
    if (scrollRef.value) {
      syncScrollXBarLeft(scrollRef.value.scrollLeft)
    }
  } finally {
    programmaticScroll = false
  }
}

async function scrollToTop() {
  if (scrollRef.value) {
    scrollRef.value.scrollTop = 0
    mainScrollTop.value = 0
  }
  await nextTick()
  virtualizer.value.scrollToIndex(0, { align: 'start' })
  await nextTick()
  virtualizer.value.measure()
}

function syncMeasuredBodyHeight() {
  measuredBodyHeight.value = scrollRef.value?.clientHeight ?? 0
}

onMounted(() => {
  $grid.bodyRef = viewportRef
  $grid.bodyScrollRef = scrollRef
  $grid.scrollToTop = () => {
    void scrollToTop()
  }
  $grid.scrollToRow = scrollToRow
  $grid.scrollToColumn = scrollToColumn
  $grid.scrollToCell = scrollToCell
  updateOverflowX()
  updateScrollbarPadding()
  syncMeasuredBodyHeight()
  if (viewportRef.value) {
    resizeObserver = new ResizeObserver(() => {
      const layoutW = resolveGridLayoutWidth(scrollRef.value)
      if (layoutW > 0) {
        $grid.scheduleColumnWidthRecalc?.(layoutW)
      }
      updateOverflowX()
      updateScrollbarPadding()
      syncMeasuredBodyHeight()
      void refreshRenderedContentWidth()
    })
    resizeObserver.observe(viewportRef.value)
  }
  if (scrollRef.value) {
    resizeObserver.observe(scrollRef.value)
    const nextLeft = clampBodyScrollLeft(scrollRef.value, scrollRef.value.scrollLeft)
    if (nextLeft !== scrollRef.value.scrollLeft) {
      setBodyScrollLeft(nextLeft)
    } else {
      mainScrollLeft.value = nextLeft
      updateScrollShadows(scrollRef.value)
    }
  }
  $grid.syncCurrentRowDom?.()
  void refreshRenderedContentWidth()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

const columns = computed(() => internalData.visibleColumns)
const tableData = computed(() => reactData.tableData)
const showEmptyState = computed(
  () =>
    !tableData.value.length &&
    !reactData.loading &&
    !reactData.loadError
)

const rowHeight = computed(() => getGridRowHeight($grid.gridSize.value))
const showFixedOverlays = computed(
  () => overflowX.value && hasFixedColumns(columns.value)
)
/** 列宽溢出时统一使用 viewport 底部独立横向滚动条（不仅限于固定列） */
const showSeparateXScroll = computed(() => overflowX.value)
const layoutColumns = computed(() => columns.value)
const tableWidth = computed(() => resolveTableWidth(layoutColumns.value))
const layoutWidth = computed(() => resolveGridLayoutWidth(scrollRef.value))
const layoutContentWidth = computed(() =>
  resolveGridBodyContentWidth(
    tableWidth.value,
    layoutWidth.value,
    overflowX.value
  )
)
const effectiveLayoutContentWidth = computed(() =>
  resolveEffectiveGridContentWidth(
    layoutContentWidth.value,
    renderedContentWidth.value
  )
)
const bodyContentStyle = computed<CSSProperties>(() => {
  const width = effectiveLayoutContentWidth.value
  if (width <= 0) {
    return {}
  }
  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    overflow: 'hidden',
  }
})
const leftPanelColumns = computed(() => resolveLeftPanelColumns(columns.value))
const rightPanelColumns = computed(() => resolveRightPanelColumns(columns.value))
const leftPanelLayoutColumns = computed(() => leftPanelColumns.value)
const leftFixedLeadingOffset = computed(() =>
  resolveLeftFixedLeadingOffset(columns.value)
)
const leftFixedPanelOffset = computed(() =>
  resolveLeftFixedPanelOffset(leftFixedLeadingOffset.value, mainScrollLeft.value)
)
const effectiveLeftFixedInset = computed(() =>
  leftFixedPanelOffset.value === 0 ? leftFixedWidth.value : 0
)
const leftFixedPanelStyle = computed<CSSProperties>(() => ({
  width: addUnit(leftFixedWidth.value),
  left: addUnit(leftFixedPanelOffset.value),
}))
const rightPanelLayoutColumns = computed(() => rightPanelColumns.value)
const leftPanelTableWidth = computed(() =>
  resolveTableWidth(leftPanelLayoutColumns.value)
)
const rightPanelTableWidth = computed(() =>
  resolveTableWidth(rightPanelLayoutColumns.value)
)
const leftFixedWidth = computed(() => computeFixedWidth(columns.value, 'left'))
const rightFixedWidth = computed(() => computeFixedWidth(columns.value, 'right'))

const bodyAreaHeight = computed(() =>
  resolveBodyAreaHeight({
    rowHeight: rowHeight.value,
    rowCount: tableData.value.length,
    height: props.height,
    showFooter: props.showFooter,
    useFixedXScroll: showSeparateXScroll.value,
    fill: fillMode.value,
  })
)

const viewportStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    '--z-grid-empty-body-height': `${DEFAULT_EMPTY_BODY_HEIGHT}px`,
    '--z-grid-body-height': `${bodyAreaHeight.value}px`,
    '--z-grid-scrollbar-width': `${verticalScrollbarWidth.value}px`,
  }

  if (props.height != null && props.height !== '') {
    style.height = addUnit(props.height)
    return style
  }

  if (fillMode.value) {
    // 铺满父容器：高度由 .is-fill flex 链决定，不写死 viewport / body 高度
    return style
  }

  style.height = addUnit(
    resolveViewportHeight({
      rowHeight: rowHeight.value,
      rowCount: tableData.value.length,
      showFooter: props.showFooter,
      useFixedXScroll: showSeparateXScroll.value,
    })
  )
  return style
})

const scrollXContentWidth = computed(() => effectiveLayoutContentWidth.value)

const fixedBodyScrollTransform = computed<CSSProperties>(() => ({
  transform: `translateY(-${mainScrollTop.value}px)`,
}))

const virtualizerOptions = computed(() => ({
  count: tableData.value.length,
  getScrollElement: () => scrollRef.value,
  estimateSize: () => rowHeight.value,
  overscan: 6,
}))

const virtualizer = useVirtualizer(virtualizerOptions)
const virtualRows = computed(() => virtualizer.value.getVirtualItems())
const totalSize = computed(() => virtualizer.value.getTotalSize())

watch(rowHeight, async () => {
  await nextTick()
  virtualizer.value.measure()
})

watch(
  () => $grid.gridSize.value,
  async () => {
    $grid.scheduleColumnWidthRecalc?.(resolveGridLayoutWidth(scrollRef.value))
    await nextTick()
    virtualizer.value.measure()
  }
)

watch(
  () => tableData.value.length,
  async () => {
    await nextTick()
    virtualizer.value.measure()
    updateScrollbarPadding()
    void refreshRenderedContentWidth()
  }
)

watch(needsVerticalScroll, async () => {
  await nextTick()
  updateScrollbarPadding()
  $grid.scheduleColumnWidthRecalc?.(resolveGridLayoutWidth(scrollRef.value))
})

watch([layoutContentWidth, tableWidth, columns], async () => {
  void refreshRenderedContentWidth()
})

watch(effectiveLayoutContentWidth, async () => {
  await nextTick()
  const el = scrollRef.value
  if (!el) {
    return
  }
  const nextLeft = clampBodyScrollLeft(el, el.scrollLeft)
  if (nextLeft !== el.scrollLeft) {
    setBodyScrollLeft(nextLeft)
  }
})

watch(verticalScrollbarWidth, () => {
  $grid.scheduleColumnWidthRecalc?.(resolveGridLayoutWidth(scrollRef.value))
})

watch([tableWidth, () => showFixedOverlays.value, showSeparateXScroll], async () => {
  await nextTick()
  updateOverflowX()
  virtualizer.value.measure()
  if (scrollRef.value) {
    const left = scrollRef.value.scrollLeft
    syncScrollXBarLeft(left)
    syncHorizontalMirrors(left)
  }
})

function updateOverflowX() {
  const el = scrollRef.value
  if (!el) {
    overflowX.value = false
    return
  }
  syncRenderedContentWidth()
  const contentWidth = Math.max(tableWidth.value, renderedContentWidth.value)
  overflowX.value = contentWidth > el.offsetWidth + 1
}

function updateScrollShadows(el: HTMLElement) {
  if (!showFixedOverlays.value) {
    leftScrolling.value = false
    rightScrolling.value = false
    return
  }
  leftScrolling.value = isLeftFixedStuck(
    leftFixedLeadingOffset.value,
    el.scrollLeft
  )
  rightScrolling.value =
    el.scrollLeft < resolveMaxBodyScrollLeft(el) - 1
}

function maybeLoadMoreOnScroll(el: HTMLElement) {
  const infinite = resolveInfiniteScroll($grid.options)
  if (!infinite.enabled) {
    return
  }
  const remain = el.scrollHeight - el.scrollTop - el.clientHeight
  if (remain > infinite.threshold) {
    return
  }
  void $grid.loadMore?.()
}

function onMainScroll(event: Event) {
  const el = event.target as HTMLElement
  mainScrollTop.value = el.scrollTop
  const nextLeft = clampBodyScrollLeft(el, el.scrollLeft)
  if (nextLeft !== el.scrollLeft) {
    el.scrollLeft = nextLeft
  }
  mainScrollLeft.value = nextLeft
  updateScrollShadows(el)
  if (showSeparateXScroll.value) {
    syncScrollXBarLeft(nextLeft)
  }
  syncHorizontalMirrors(nextLeft)
  updateScrollbarPadding()
  maybeLoadMoreOnScroll(el)
  $grid.closeContextMenu?.()
}

function onScrollXBar(event: Event) {
  const el = event.target as HTMLElement
  syncBodyScrollLeftFromBar(el.scrollLeft)
}
</script>

